import React from "react";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalBody } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalBody";
import { Trans, useTranslation } from "@coworker/locales";
import styles from "./multi-select.module.css";
import SkapaTabs from "@coworker/app/src/components/InputPopup/SkapaTabs";
import { SelectedTasks } from "./SelectedTasks";
import { useActivityData } from "../../../../hooks/useActivityData";
import { TaskCard as TaskCardV1 } from "../../../../../tasks/components/TaskCard/TaskCard";
import { useItemsInfoQueries } from "../../../../../shared/hooks/item/useItemsInfo";
import { Item } from "../../../../../shared/types/item";
import FixaButton from "../../../../../shared/wrappers/FixaButton/FixaButton";
import { LoadMore } from "../../../../../shared/components/LoadMore/LoadMore";
import { useMultiselectOverflowMenu } from "@coworker/app/src/features/tasks/useMultiselectOverflowMenu";
import { useMultiSelectForTab } from "@coworker/app/src/hooks/useMultiSelect";
import { useInputPopup } from "@coworker/app/src/components/InputPopup";
import { MultiselectReturnType } from "@coworker/app/src/types/multiselectReturnType";
import { SimpleLoadingBall } from "../../../../../shared/simple/SimpleLoading/SimpleLoading";
import { FullTask } from "../../../../types/FullTask";
import { ActivityColumns } from "../../../../constants/activityColumns";
import { ModalContext } from "../../../../../layout/context/ModalContextProvider";
import { ModalType } from "../../../../../layout/context/modalType";
import { ActivityFilterType } from "../../../../types/filterOptionsTypes";
import { TaskType } from "@coworker/apprestructured/src/tasks/enums/taskTypes";
import TaskCardV2 from "@coworker/apprestructured/src/task-management/components/TaskCard/TaskCard";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { TaskListItem } from "@coworker/apprestructured/src/activity/hooks/useActivityData";
import { SelectableTaskCardWrapper } from "@coworker/apprestructured/src/shared/wrappers/SelectableTaskCardWrapper/SelectableTaskCardWrapper";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";

interface MultiSelectPanelProps {
  filters: ActivityFilterType;
  onShowTaskDetails: (taskId: string, taskType: TaskType) => void;
}

export const MultiSelectPanel = ({
  filters,
  onShowTaskDetails,
}: MultiSelectPanelProps) => {
  const { t } = useTranslation();
  const { modalState, toggleModal } = React.useContext(ModalContext);
  const tab = modalState.props?.type?.type || "";
  const [activeTab, setActiveTab] = React.useState<"my" | "open">("my");
  const uid = useUID();

  React.useEffect(() => {
    setActiveTab(tab === "open" ? "open" : "my");
  }, [tab]);

  const myTasks =
    useActivityData(
      { ...filters, assignedTo: uid },
      ActivityColumns.IN_PROGRESS,
      modalState.isMultiSelectTasksModalOpen
    )?.inProgressTasks ?? [];
  const openTasks =
    useActivityData(
      filters,
      ActivityColumns.OPEN,
      modalState.isMultiSelectTasksModalOpen
    )?.openTasks ?? [];

  const allTasks = React.useMemo(
    () => ({
      my: myTasks.data?.pages.flatMap((page) => page.list) ?? [],
      open: openTasks.data?.pages.flatMap((page) => page.list) ?? [],
    }),
    [myTasks.data?.pages, openTasks.data?.pages]
  );

  const tabs = [
    {
      title: `${t("myString")} (${myTasks.data?.pages[0]?.filteredCount})`,
      key: "my",
    },
    {
      title: `${t("openTasksString")} (${
        openTasks.data?.pages[0]?.filteredCount
      })`,
      key: "open",
    },
  ];

  const itemNos = [...allTasks.my, ...allTasks.open]
    .filter(({ type }) => type === "v1")
    .map(({ task_data }) => (task_data as FullTask).product_article_id)
    .filter(Boolean);
  const itemsInfoResult = useItemsInfoQueries(itemNos);

  const itemsInfo = itemsInfoResult
    .map((result) => result.data)
    .filter(Boolean);

  const { selectedTasksCount, selectedTaskIds, toggle, reset } =
    useMultiSelectForTab(activeTab) as MultiselectReturnType;

  const { selectedV1TaskIds, selectedV2TaskIds } = React.useMemo(() => {
    const v1Ids: string[] = [];
    const v2Ids: string[] = [];

    selectedTaskIds.forEach((taskId) => {
      const task = allTasks[activeTab].find((t) => t.task_data.id === taskId);
      if (task?.type === "v1") {
        v1Ids.push(taskId);
      } else if (task?.type === "v2") {
        v2Ids.push(taskId);
      }
    });

    return { selectedV1TaskIds: v1Ids, selectedV2TaskIds: v2Ids };
  }, [selectedTaskIds, allTasks, activeTab]);

  const { getInput } = useInputPopup();
  const { getTaskMenu, loading } = useMultiselectOverflowMenu(
    selectedTasksCount,
    activeTab,
    selectedV1TaskIds,
    selectedV2TaskIds,
    reset
  );

  const [, setSearchParams] = useSearchParamState();

  const handleTaskV2CardClick = React.useCallback(
    (taskId: string) => {
      setSearchParams((params) => {
        params.set("taskViewer", String(taskId));
      });
    },
    [setSearchParams]
  );

  const handleCheckboxSelect = React.useCallback(
    (taskId: string) => {
      if (toggle) toggle(taskId);
    },
    [toggle]
  );

  const warningPopup = React.useCallback(async () => {
    const response = await getInput("confirmation", {
      question: <Trans count={selectedTasksCount}>aboutToChangeString</Trans>,
      description: (
        <>
          <Trans>undoNotPossibleString</Trans>
          <span>
            <Trans>sureToContinueString</Trans>
          </span>
        </>
      ),
      positiveText: <Trans>yesString</Trans>,
      negativeText: <Trans>negativeString</Trans>,
    });
    if (response) await getTaskMenu();
  }, [getInput, selectedTasksCount, getTaskMenu]);

  const handleSelectedTasks = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (selectedTasksCount > 14) {
      await warningPopup();
    } else {
      await getTaskMenu();
    }
  };

  const renderTasks = (
    tasks: TaskListItem[],
    isFetching: boolean,
    fetchNextPage: () => void,
    totalCount: number
  ) => {
    if (!tasks.length) {
      return (
        <div className={styles["no-results"]}>
          <div className={styles["no-results-title"]}>
            <Trans>noTaskResultsString</Trans>
          </div>
          <div className={styles["no-results-subtitle"]}>
            <Trans>noTasksFoundString</Trans>
          </div>
        </div>
      );
    }

    return (
      <>
        <SelectedTasks type={activeTab} tasks={allTasks[activeTab]} />
        {tasks.map((taskListItem) => {
          const { type, task_data: task } = taskListItem;

          if (type === "v1") {
            return (
              <TaskCardV1
                key={`v1_${task.id}`}
                task={task as FullTask}
                multiSelect
                type={activeTab}
                item={
                  itemsInfo?.find(
                    (item) => item?.no === (task as FullTask).product_article_id
                  ) as Item
                }
                onShowTaskDetails={onShowTaskDetails}
              />
            );
          }

          if (type === "v2") {
            return (
              <SelectableTaskCardWrapper
                key={`v2_${task.id}`}
                value={task.id}
                selected={selectedTaskIds.includes(task.id)}
                onCardClick={handleTaskV2CardClick}
                onCheckboxSelect={handleCheckboxSelect}
              >
                <TaskCardV2 task={task} />
              </SelectableTaskCardWrapper>
            );
          }

          return null;
        })}
        {tasks.length < totalCount && (
          <LoadMore
            onLoadMoreClick={fetchNextPage}
            loadedItemsCount={tasks.length}
            totalCount={totalCount}
            isFetching={isFetching}
          />
        )}
      </>
    );
  };

  return (
    <FixaModal
      visible={modalState.isMultiSelectTasksModalOpen}
      handleCloseBtn={() => toggleModal({ type: ModalType.MultiSelectTasks })}
      className={styles["filters-panel"] as string}
    >
      <FixaSheets
        alignment="right"
        preserveAlignment={false}
        size="small"
        fullSize
        header={
          <FixaModalHeader
            title={t("multiSelectTasksString")}
            className={styles["header"] as string}
            floating={false}
          />
        }
        footer={
          selectedTasksCount > 0 ? (
            <FixaModalFooter className={styles["footer"] as string}>
              <FixaButton
                text={t("continue2String")}
                type="primary"
                onClick={handleSelectedTasks}
              />
            </FixaModalFooter>
          ) : null
        }
      >
        <FixaModalBody>
          <SkapaTabs
            tabs={tabs}
            active={activeTab}
            onTabChanged={setActiveTab}
            noTabPanelPadding
          />
          {loading ? (
            <div className={styles["loading"]}>
              <SimpleLoadingBall
                text={t("loadingEllipsisString")}
                size="medium"
              />
            </div>
          ) : (
            <>
              {activeTab === "my" &&
                renderTasks(
                  allTasks.my,
                  myTasks.isFetching,
                  myTasks.fetchNextPage,
                  myTasks.data?.pages[0]?.filteredCount || 0
                )}
              {activeTab === "open" &&
                renderTasks(
                  allTasks.open,
                  openTasks.isFetching,
                  openTasks.fetchNextPage,
                  openTasks.data?.pages[0]?.filteredCount || 0
                )}
            </>
          )}
        </FixaModalBody>
      </FixaSheets>
    </FixaModal>
  );
};
